import { z } from 'zod';
import { isRealDate, paginationQuerySchema, uuidSchema } from './common.js';
import { PAYMENT_METHODS } from './fees.js';
import { periodSchema } from './transport.js';
const text = (max) => z
    .string()
    .trim()
    .max(max)
    .refine((v) => !v.includes('\u0000'), 'Invalid character');
const optionalText = (max) => text(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine(isRealDate, 'Invalid calendar date');
const atLeastOne = (v) => Object.keys(v).length > 0;
const MAX_MONEY = 1_000_000_000_000;
/** Positive whole units of money; one line may not exceed what the ledger can hold. */
const unitCost = z.number().int().min(1).max(MAX_MONEY);
const positiveQuantity = z.number().int().min(1).max(1_000_000_000);
const flag = z.enum(['true', 'false']).transform((v) => v === 'true');
/** quantity x unit cost must stay inside the money range, so a total can never overflow. */
const fitsMoney = (v) => v.unitCostMinor === undefined || Math.abs(v.quantity) * v.unitCostMinor <= MAX_MONEY;
const fitsMoneyIssue = { message: 'The total value is too large', path: ['unitCostMinor'] };
// ----------------------------------------------------------------- suppliers
const supplierFields = {
    name: text(200).min(1),
    contactPerson: optionalText(200),
    phone: optionalText(30),
    email: optionalText(200),
    address: optionalText(500),
    status: z.enum(['active', 'inactive']),
};
export const createSupplierSchema = z.object({
    ...supplierFields,
    status: supplierFields.status.default('active'),
});
export const updateSupplierSchema = z
    .object(supplierFields)
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const listSuppliersQuerySchema = paginationQuerySchema.extend({
    q: text(50).optional(),
    status: z.enum(['active', 'inactive']).optional(),
});
export const supplierPaymentSchema = z.object({
    amountMinor: unitCost,
    method: z.enum(PAYMENT_METHODS),
    reference: optionalText(100),
    paidOn: isoDate.optional(),
});
// -------------------------------------------------------------------- items
const itemFields = {
    sku: text(40).min(1),
    name: text(200).min(1),
    unit: text(20).min(1),
    category: optionalText(100),
    reorderLevel: z.number().int().min(0).max(1_000_000_000),
    status: z.enum(['active', 'inactive']),
};
export const createItemSchema = z.object({
    ...itemFields,
    unit: itemFields.unit.default('each'),
    reorderLevel: itemFields.reorderLevel.default(0),
    status: itemFields.status.default('active'),
});
export const updateItemSchema = z
    .object(itemFields)
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const listItemsQuerySchema = paginationQuerySchema.extend({
    q: text(50).optional(),
    category: text(100).optional(),
    status: z.enum(['active', 'inactive']).optional(),
    lowOnly: flag.optional(),
});
// ----------------------------------------------------------------- movements
export const receiveStockSchema = z
    .object({
    itemId: uuidSchema,
    supplierId: uuidSchema,
    quantity: positiveQuantity,
    unitCostMinor: unitCost,
    reference: optionalText(100),
    occurredOn: isoDate.optional(),
    note: optionalText(300),
})
    .refine(fitsMoney, fitsMoneyIssue);
export const issueStockSchema = z.object({
    itemId: uuidSchema,
    quantity: positiveQuantity,
    issuedTo: text(200).min(1),
    issuedToStaffId: uuidSchema.nullable().optional(),
    occurredOn: isoDate.optional(),
    note: optionalText(300),
});
/** A count correction: positive adds stock (at the given or current average cost), negative writes stock off. */
export const adjustStockSchema = z
    .object({
    itemId: uuidSchema,
    quantity: z
        .number()
        .int()
        .min(-1_000_000_000)
        .max(1_000_000_000)
        .refine((q) => q !== 0, 'The change cannot be zero'),
    unitCostMinor: unitCost.optional(),
    occurredOn: isoDate.optional(),
    note: text(300).min(1),
})
    .refine(fitsMoney, fitsMoneyIssue);
export const listMovementsQuerySchema = paginationQuerySchema.extend({
    itemId: uuidSchema.optional(),
    supplierId: uuidSchema.optional(),
    kind: z.enum(['receipt', 'issue', 'adjustment']).optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
});
// -------------------------------------------------------------------- assets
export const ASSET_STATUSES = ['in_use', 'in_store', 'under_repair', 'disposed'];
export const createAssetSchema = z
    .object({
    name: text(200).min(1),
    category: optionalText(100),
    serialNumber: optionalText(100),
    location: optionalText(200),
    custodianStaffId: uuidSchema.nullable().optional(),
    supplierId: uuidSchema,
    purchaseDate: isoDate,
    costMinor: unitCost,
    salvageMinor: z.number().int().min(0).max(MAX_MONEY).default(0),
    usefulLifeMonths: z.number().int().min(1).max(600),
    notes: optionalText(500),
})
    .refine((v) => v.salvageMinor < v.costMinor, {
    message: 'The salvage value must be less than the cost',
    path: ['salvageMinor'],
});
export const updateAssetSchema = z
    .object({
    name: text(200).min(1),
    category: optionalText(100),
    serialNumber: optionalText(100),
    location: optionalText(200),
    custodianStaffId: uuidSchema.nullable(),
    status: z.enum(['in_use', 'in_store', 'under_repair']),
    notes: optionalText(500),
})
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const listAssetsQuerySchema = paginationQuerySchema.extend({
    q: text(50).optional(),
    status: z.enum(ASSET_STATUSES).optional(),
    category: text(100).optional(),
    custodianStaffId: uuidSchema.optional(),
});
export const disposeAssetSchema = z
    .object({
    disposedOn: isoDate.optional(),
    proceedsMinor: z.number().int().min(0).max(MAX_MONEY),
    method: z.enum(PAYMENT_METHODS).optional(),
    note: text(300).min(1),
})
    .refine((v) => v.proceedsMinor === 0 || v.method !== undefined, {
    message: 'Say how the proceeds were received',
    path: ['method'],
});
export const depreciateSchema = z.object({ period: periodSchema });
//# sourceMappingURL=inventory.js.map